import { PubSubService } from '@ohif/core';

/**
 * Vendored StateSyncService.
 *
 * OHIF 3.9 removed the core `StateSyncService` in favour of Zustand stores
 * (see migration-guide/3p8-to-3p9/5-StateSyncService). Gleamer still relies on
 * the old register/getState/store API for its own state slices (`taskLayout`,
 * `previousHangingProtocol`, `previousPresentationId`, `slabThickness`), so we
 * vendor the original implementation here and register it as a Gleamer service.
 *
 * NOTE: `positionPresentationStore` is pre-registered as a default slice so the
 * MPR/MIP commands in getCommandsModule keep working without crashing. It is a
 * Gleamer-local copy and is NOT synced with OHIF's `usePositionPresentationStore`
 * Zustand store — fully wiring MPR camera restore to the new store is a separate
 * Cluster-1 (MPR/MIP) follow-up.
 */

const EVENTS = {};

type Obj = Record<string, unknown>;

type StateConfig = {
  /** clearOnModeExit clears this slice automatically on exiting a mode. The
   * clearing occurs after the mode onModeExit, so state can be preserved during
   * exit to be restored later. */
  clearOnModeExit?: boolean;
};

type States = {
  [key: string]: Obj;
};

export default class StateSyncService extends PubSubService {
  public static REGISTRATION = {
    name: 'stateSyncService',
    create: ({ configuration = {} }) => {
      return new StateSyncService({ configuration });
    },
  };

  configuration: Obj;
  registeredStateSets: {
    [id: string]: StateConfig;
  } = {};
  state: States = {};

  constructor({ configuration }: { configuration?: Obj }) {
    super(EVENTS);
    this.configuration = configuration || {};
    // Default slice previously provided by core; MPR/MIP commands read/write it.
    this.register('positionPresentationStore', {});
  }

  public init(): void {}

  /** Registers a new sync store called `id`. A default store has the lifetime of
   * the application; a `clearOnModeExit` store is cleared on mode exit. */
  public register(id: string, config: StateConfig): void {
    this.registeredStateSets[id] = config;
    this.store({ [id]: {} });
  }

  public getState(): Record<string, Obj> {
    return this.state;
  }

  /** Stores all the new state values contained in `states`. */
  public store(states: States): States {
    Object.keys(states).forEach(stateKey => {
      if (!this.registeredStateSets[stateKey]) {
        throw new Error(`No state ${stateKey} registered`);
      }
    });
    this.state = { ...this.state, ...states };
    return states;
  }

  public onModeExit(): void {
    const toReduce = {};
    for (const [key, value] of Object.entries(this.registeredStateSets)) {
      if (value.clearOnModeExit) {
        toReduce[key] = {};
      }
    }
    this.store(toReduce);
  }
}
